import { SuspenseLoading } from '@/components/SuspenseErrorBoundary/fallback';
import {
  useAgreeAiService,
  useGetAiServiceSetting,
  useUpdateModelTrainingSetting,
} from '@/context/services/ai_service_setting/AiServiceSetting.service';
import { useErrorTrackingSystem } from '@/hooks/useErrorTrackingSystem';
import { useTranslation } from '@/i18n';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { FC, Suspense, useCallback } from 'react';
import { AiServiceSettings } from './AiServiceSettings/AiServiceSettings';
import { InitialSetting } from './InitialSetting/InitialSetting';
import styles from './styles.module.scss';

export const InnerAiServiceSetting: FC = () => {
  const setting = useGetAiServiceSetting();
  const { isPending: isAgreementPending, mutateAsync: agreeAiService } =
    useAgreeAiService();
  const {
    isPending: isUpdateModelTrainingSettingPending,
    mutateAsync: updateModelTrainingSetting,
  } = useUpdateModelTrainingSetting();
  const { t } = useTranslation();
  const notify = useToastAPI();
  const { send } = useErrorTrackingSystem();

  const notifySuccess = useCallback(
    (message: string) => {
      notify.success({
        duration: 5,
        title: message,
      });
    },
    [notify]
  );

  const notifyError = useCallback(
    (message: string) => {
      notify.error({
        duration: 5,
        title: message,
      });
    },
    [notify]
  );

  const handleAgreement = async (isModelTrainingAllowed: boolean) => {
    try {
      const result = await agreeAiService(isModelTrainingAllowed);

      if (result.isSuccessful) {
        notifySuccess(t('ai_service_setting.agreement_success'));
        return;
      }

      notifyError(result.message);
    } catch (error) {
      send(error instanceof Error ? error : String(error));
      notifyError(t('ai_service_setting.agreement_error'));
    }
  };

  const handleModelTrainingAllowedChange = async (isAllowed: boolean) => {
    try {
      const result = await updateModelTrainingSetting(isAllowed);

      if (result.isSuccessful) {
        notifySuccess(
          t(
            isAllowed
              ? 'ai_service_setting.model_training.allowed_success'
              : 'ai_service_setting.model_training.disallowed_success'
          )
        );
        return;
      }

      notifyError(result.message);
    } catch (error) {
      send(error instanceof Error ? error : String(error));
      notifyError(t('ai_service_setting.model_training.update_error'));
    }
  };

  if (setting.is_ai_service_agreed) {
    return (
      <AiServiceSettings
        isModelTrainingAllowed={setting.is_model_training_allowed}
        isModelTrainingUpdatePending={isUpdateModelTrainingSettingPending}
        onModelTrainingAllowedChange={handleModelTrainingAllowedChange}
        services={setting.ai_service_settings}
      />
    );
  } else {
    return (
      <InitialSetting
        isAgreementPending={isAgreementPending}
        initialIsModelTrainingAllowed={setting.is_model_training_allowed}
        onAgreement={handleAgreement}
        services={setting.ai_service_settings}
      />
    );
  }
};

export const AiServiceSetting: FC = () => (
  <Suspense fallback={<SuspenseLoading className={styles.loadingContainer} />}>
    <InnerAiServiceSetting />
  </Suspense>
);
